import IContentDialogService from 'features/shared/components/contentDialog/IContentDialogService';
import InfoItem from 'features/shared/components/infoItem/InfoItem';
import { Currency } from 'features/shared/enums/currency';
import { formatDate, formatNumber, formatPrice } from 'features/shared/helpers/Formatters';
import { useInjection } from 'infrastructure/ioc/ioc.react';
import { IocTypes } from 'infrastructure/ioc/ioc.types';
import React from 'react';
import { useTranslation } from 'react-i18next';
import RequirementDescriptorDTO from '../dtos/RequirementDescriptorDTO';
import { DescriptorType } from '../enums/descriptorType';

interface Props {
  descriptor: RequirementDescriptorDTO;
}

const RequirementDescriptor = ({ descriptor }: Props) => {
  // DI
  const dialogService = useInjection<IContentDialogService>(IocTypes.ContentDialogService);

  // translations
  const { t } = useTranslation(["shared\\terms"]);

  // helpers
  const getFormattedValue = () => {
    switch (getDescriptorType(descriptor.dataType)) {
      case DescriptorType.Date:
        return formatDate(descriptor.valueExtended!);
      case DescriptorType.DateTime:
        return formatDate(descriptor.valueExtended!, { hour: "numeric", minute: "numeric" });
      case DescriptorType.String:
        return descriptor.valueExtended;
      case DescriptorType.StringLong:
        return getLongStringElement(descriptor.label, descriptor.valueExtended);
      case DescriptorType.Number:
        let numberFormat = getTypeInfoFormat(descriptor.dataType);
        return formatNumber(Number(descriptor.valueExtended), Number(numberFormat[0]));
      case DescriptorType.Bool:
        return getBooleanValue(descriptor.valueExtended);
      case DescriptorType.Currency:
        let priceFormat = getTypeInfoFormat(descriptor.dataType);
        return formatPrice(Number(descriptor.valueExtended), priceFormat[0] as Currency, Number(priceFormat[1]));
      default:
        return "-";
    }
  }

  const getDescriptorType = (type?: string) => {
    let matches = type ? type.match(/(?:\w+)/) : null;
    if (matches !== null) {
      return matches[0].toString();
    } else {
      return "";
    }
  }

  const getTypeInfoFormat = (type?: string) => {
    let matches = type ? type.match(/\((.*?)\)/) : null;
    if (matches) {
      return matches[1].split(",");
    } else {
      return [];
    }
  }
  
  const getBooleanValue = (boolValue?: string) => {
    if (boolValue) {
      let boolStr = boolValue.trim().toLocaleLowerCase() === "true" ? "Yes" : "No";
      return t(boolStr);
    } else {
      return "-";
    }
  }

  const getLongStringElement = (label?: string, text?: string) => {
    if (label && text) {
      const openModalHandler = () => {
        dialogService.openDialog(label, () => <div className="m-4">{text}</div>);
      }

      let truncatedText = text.length > 40 ? `${text.substr(0, 40)}...` : text;

      return (
        <div className="text-truncat font-weight-normal" onClick={openModalHandler} role="button">
          {truncatedText}
        </div>
      );
    }
  }

  // render
  return (
    <InfoItem title={descriptor.label!} value={getFormattedValue()} />
  )
}

export default RequirementDescriptor;
